import { Box, Pagination, Typography } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { ListingCard } from '../../components';
import { useDispatch, useSelector } from 'react-redux';
import { useConfiguration } from '../../context/configurationContext';
import { fetchListings, setBounds, resetListings } from './DynamicPage.duck';
import { decodeLatLngBounds } from '../../util/urlHelpers';
import { dedupeAndRankListings, isVisibleListing } from '../../util/listingFiltering';

const decodeBoundsProp = boundsProp => {
  if (!boundsProp) return null;
  if (typeof boundsProp !== 'string') return boundsProp;
  const decoded = decodeLatLngBounds(boundsProp);
  if (!decoded) {
    console.warn('[DynamicPageListings] could not decode bounds string:', boundsProp);
  }
  return decoded;
};

export default function DynamicPageListings({ bounds: boundsProp, heading = 'Listings' }) {
  const [page, setPage] = React.useState(1);
  const { listings, meta, bounds: storeBounds, loadingListings, loadingBounds } = useSelector(
    state => state.DynamicPage
  );
  const dispatch = useDispatch();
  const location = useLocation();
  const config = useConfiguration();

  useEffect(() => {
    let cancelled = false;
    // Always reset before fetching so stale listings from a previously
    // visited city page never leak into the current view.
    dispatch(resetListings());
    setPage(1);

    (async () => {
      try {
        let activeBounds = decodeBoundsProp(boundsProp);

        if (!activeBounds && boundsProp) {
          // Caller passed a bounds string but we failed to decode it — bail
          // rather than firing an unbounded query that returns "all" listings.
          return;
        }
        if (!activeBounds && location?.pathname) {
          activeBounds = await dispatch(setBounds(location.pathname, config));
        }
        if (cancelled || !activeBounds) return;

        await dispatch(fetchListings({ bounds: activeBounds, page: 1 }, config));
      } catch (e) {
        console.warn('[DynamicPageListings] fetch failed:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [boundsProp, location?.pathname, dispatch, config]);

  const displayListings = useMemo(() => {
    const visible = listings.filter(isVisibleListing);
    const filtered = visible.filter(l => l?.attributes?.title !== 'Location variant');
    return dedupeAndRankListings(filtered);
  }, [listings]);

  const handlePageChange = (_, value) => {
    setPage(value);
    const paginationBounds = decodeBoundsProp(boundsProp) || storeBounds;
    if (!paginationBounds) return;
    dispatch(fetchListings({ page: value, bounds: paginationBounds }, config));
  };

  const pageCount = meta.totalPages;
  const isLoading = loadingListings || loadingBounds;

  if (!isLoading && displayListings.length === 0) {
    return null;
  }

  return (
    <Box>
      {heading ? (
        <Typography variant="h4" color="var(--colorGrey700)" fontSize="1.5rem">
          {heading}
        </Typography>
      ) : null}

      <Box display="flex" flexWrap="wrap" gap="2rem" mt={heading ? '1rem' : 0}>
        {displayListings.map(listing => (
          <Box
            sx={{
              width: {
                xs: '100%',
                sm: `calc((100% / 2) - 1rem)`,
                md: `calc((100% / 3) - 1.34rem)`,
              },
              opacity: isLoading ? 0.5 : 1,
            }}
            key={listing.id.uuid}
          >
            <ListingCard listing={listing} />
          </Box>
        ))}
      </Box>
      {pageCount > 1 && (
        <Box display="flex" justifyContent="center" paddingTop="3rem">
          <Pagination page={page} onChange={handlePageChange} color="primary" count={pageCount} />
        </Box>
      )}
    </Box>
  );
}
